import React from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';

import { formatCurrencyDollars } from '../../helpers/formatters';

type CardLinkProps = {
  href?: string | null;
  ariaLabel: string;
  analyticsName: string;
  className?: string;
  children?: React.ReactNode;
};

export const CardLink: React.FC<CardLinkProps> = ({
  href,
  ariaLabel,
  analyticsName,
  className,
  children,
}) => {
  if (href == null) {
    return <Col className={className}>{children}</Col>;
  }

  return (
    <Col href={href} aria-label={ariaLabel} analyticsName={analyticsName} className={className}>
      {children}
    </Col>
  );
};

export type PropertyCardProps = {
  beds?: number | null;
  baths?: number | null;
  city?: string | null;
  dwellingType?: string | null;
  href?: string | null;
  isFavorite?: boolean | null;
  isFavoriteLoading?: boolean;
  neighborhood?: string | null;
  onFavoritePress?: () => void;
  isPhotoPending?: boolean;
  photoMedia?: React.ReactElement;
  photoUrl?: string | null;
  /**
   * Rendered inside the photo container, on top of the photo — badges, hover
   * treatments, etc. Position children with `absolute`; the container is
   * `relative` and clips to the photo's rounded bounds, so consumers never
   * have to mirror the card's aspect ratio or radius.
   */
  photoOverlay?: React.ReactNode;
  photoFetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  price?: number | null;
  /** Shows a "Self-tour available" badge next to the price. */
  selfTourAvailable?: boolean | null;
  sqft?: number | null;
};

const PROPERTY_CARD_IMAGE_CLASS = 'aspect-[16/9] w-full rounded-2xl object-cover';

export const PropertyCard: React.FC<PropertyCardProps> = ({
  beds,
  baths,
  city,
  dwellingType,
  href,
  isFavorite,
  isFavoriteLoading,
  neighborhood,
  onFavoritePress,
  isPhotoPending,
  photoMedia,
  photoUrl,
  photoFetchPriority,
  photoOverlay,
  price,
  selfTourAvailable,
  sqft,
}) => {
  return (
    <Col className="w-full gap-5">
      {photoMedia ? (
        <Col className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-od-bg-secondary">
          {photoMedia}
          {photoOverlay}
        </Col>
      ) : (
        <CardLink
          href={href}
          ariaLabel="Main view of property"
          analyticsName="marketplace-property-card-image-link"
          className="relative w-full overflow-hidden rounded-2xl"
        >
          <PropertyPhotoImage
            alt="Main view of property"
            loading="lazy"
            containerClassName={PROPERTY_CARD_IMAGE_CLASS}
            className="h-full w-full object-cover"
            placeholderClassName="bg-od-bg-tertiary"
            isPending={isPhotoPending}
            src={photoUrl}
            fetchPriority={photoFetchPriority}
          />
          {photoOverlay}
        </CardLink>
      )}
      <Col className="gap-3">
        <Row className="items-center justify-between">
          <Row className="items-center gap-2">
            {price != null && (
              <Text as="span" className="text-subheader-sm text-od-content-primary">
                {formatCurrencyDollars(price)}
              </Text>
            )}
            {selfTourAvailable && (
              <Box className="rounded-full bg-od-bg-accent-brand px-2 py-1">
                <Text as="span" className="text-label-xs text-od-content-inverse-primary">
                  Self-tour
                </Text>
              </Box>
            )}
          </Row>
          {onFavoritePress && (
            <Row className="justify-end self-center">
              <Button
                variant="secondary"
                aria-label={isFavorite ? 'unfavorite' : 'favorite'}
                analyticsName="marketplace-property-card-favorite-button"
                onClick={onFavoritePress}
                loading={isFavoriteLoading}
                className="size-6 rounded-full border-0 bg-transparent p-0"
              >
                {isFavorite ? (
                  <IconHeartFilled size={20} className="text-od-content-error" />
                ) : (
                  <IconHeart size={20} />
                )}
              </Button>
            </Row>
          )}
        </Row>
        <CardLink
          href={href}
          ariaLabel="Property details"
          analyticsName="marketplace-property-card-info-link"
          className="w-full"
        >
          <Text as="span" className="text-body-sm text-od-content-tertiary">
            {[
              beds != null ? `${beds} bds` : null,
              baths != null ? `${baths} ba` : null,
              sqft != null ? `${sqft.toLocaleString('en-US')} sqft` : null,
              dwellingType,
            ]
              .filter(Boolean)
              .join(' • ')}
          </Text>
          <Text as="span" className="text-body-sm text-od-content-tertiary">
            {[neighborhood, city].filter(Boolean).join(', ')}
          </Text>
        </CardLink>
      </Col>
    </Col>
  );
};
